import { useEffect, useState } from "react";
import { Clock, AlertTriangle, CheckCircle2, Loader2, RefreshCw, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const SLA = {
  vencido: { label: "Vencido", cls: "bg-red-100 text-red-700", dot: "#dc2626" },
  critico: { label: "Crítico", cls: "bg-orange-100 text-orange-700", dot: "#ea580c" },
  por_vencer: { label: "Por vencer", cls: "bg-amber-100 text-amber-700", dot: "#d97706" },
  a_tiempo: { label: "A tiempo", cls: "bg-emerald-100 text-emerald-700", dot: "#059669" },
  sin_sla: { label: "Sin SLA", cls: "bg-navy/5 text-navy/50", dot: "#94a3b8" },
};

const PERIODOS = [["", "Todos"], ["dia", "Día"], ["semana", "Semana"], ["quincena", "Quincena"], ["mes", "Mes"], ["trimestre", "Trimestre"], ["semestre", "Semestre"], ["anio", "Año"]];

const fmtHoras = (h) => {
  if (h == null) return "—";
  const neg = h < 0; const abs = Math.abs(h);
  const txt = abs >= 24 ? `${(abs / 24).toFixed(1)} d` : `${abs.toFixed(1)} h`;
  return neg ? `-${txt}` : txt;
};

export default function FieldSLA() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [periodo, setPeriodo] = useState("");
  const [pdf, setPdf] = useState("");

  const load = () => { setLoading(true); api.get("/field/sla", { params: periodo ? { periodo } : {} }).then((r) => setD(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [periodo]); // eslint-disable-line

  const downloadReport = async (tipo) => {
    setPdf(tipo);
    try {
      const url = tipo === "incidents" ? "/field/incidents/report.pdf" : "/field/sla/report.pdf";
      const res = await api.get(url, { params: periodo ? { periodo } : {}, responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = href; a.download = `${tipo === "incidents" ? "Incidencias" : "SLA"}-${periodo || "total"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
    } catch { toast.error("No se pudo generar el PDF"); } finally { setPdf(""); }
  };

  const r = d?.resumen || {};
  const rows = (d?.tickets || []).filter((x) => !filter || x.estado_sla === filter);
  const chips = [["", "Todos", r.abiertos || 0], ["vencido", "Vencidos", r.vencido || 0], ["critico", "Críticos", r.critico || 0], ["por_vencer", "Por vencer", r.por_vencer || 0], ["a_tiempo", "A tiempo", r.a_tiempo || 0]];

  return (
    <div data-testid="field-sla">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-navy/40">Período:</span>
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="field-sm" data-testid="sla-periodo">
            {PERIODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadReport("sla")} disabled={pdf === "sla"} className="btn btn-outline text-sm" data-testid="sla-report-pdf">
            {pdf === "sla" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Informe SLA
          </button>
          <button onClick={() => downloadReport("incidents")} disabled={pdf === "incidents"} className="btn btn-outline text-sm" data-testid="incidents-report-pdf">
            {pdf === "incidents" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Informe Incidencias
          </button>
          <button onClick={load} className="btn btn-ghost text-sm"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {chips.map(([v, l, n]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`sla-chip-${v || "all"}`}
            className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${filter === v ? "bg-navy text-cream border-navy" : "border-navy/15 text-navy/60 hover:border-navy/40"}`}>
            {l} <span className="opacity-70">· {n}</span>
          </button>
        ))}
      </div>

      {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        : rows.length === 0 ? <div className="card-surface p-8 text-center text-sm text-navy/40">Sin tickets abiertos en este filtro.</div>
          : (
            <div className="card-surface overflow-hidden">
              {rows.map((t) => {
                const s = SLA[t.estado_sla] || SLA.sin_sla;
                return (
                  <div key={t.id} data-testid={`sla-row-${t.folio || t.id}`} className="flex items-center gap-3 px-4 py-3 border-b border-navy/5 last:border-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.dot }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{t.folio || "Ticket"} · {t.empresa}</div>
                      <div className="text-[11px] text-navy/40 truncate">{t.tipo}{t.ciudad ? ` · ${t.ciudad}` : ""}{t.tecnico ? ` · ${t.tecnico}` : " · sin asignar"}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-sm font-medium flex items-center gap-1 justify-end ${t.estado_sla === "vencido" ? "text-red-600" : t.estado_sla === "critico" ? "text-orange-600" : "text-navy/70"}`}>
                        {t.estado_sla === "vencido" ? <AlertTriangle className="w-3.5 h-3.5" /> : t.estado_sla === "a_tiempo" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Clock className="w-3.5 h-3.5" />}
                        {fmtHoras(t.horas_restantes)}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
    </div>
  );
}
